import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  PlayCircleOutlined,
  OrderedListOutlined,
  PlusOutlined,
  LeftCircleOutlined,
  RightCircleOutlined
} from "@ant-design/icons";
import {
  Button,
  Card,
  Descriptions,
  Grid,
  Layout,
  List,
  Select,
  Typography,
  Space,
  Popconfirm,
  Tag,
  Divider,
  Row,
  Col
} from "antd";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";

import CourseModuleLinkFormModal from "./CourseModuleLinkModal";
import { showErrorNotification, showSuccessNotification } from "../../../utils/notification";
import { clamp } from "../../../utils/responsiveHelpers";
import LinkedEntityField from "../LinkedEntityField";

const { Content } = Layout;
const { Title, Text } = Typography;

const MODULE_LINKING_PATH = "admin/courses/manage/module-linking";

const CourseModulesLinkingDashboard = ({ ROOT_URL }) => {
  const [courses, setCourses] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const [modulesByCourse, setModulesByCourse] = useState({});
  const [selectedCourseUniquePublicId, setSelectedCourseUniquePublicId] = useState(null);

  const [isOpenCourseModuleLinkModal, setIsOpenCourseModuleLinkModal] = useState(false);
  const [activeLinkData, setActiveLinkData] = useState(null);
  const [loading, setLoading] = useState(false);

  const screens = Grid.useBreakpoint();
  const isMobileView = screens.lg === false;

  const getApiUrl = (endpoint) => `${ROOT_URL}/${MODULE_LINKING_PATH}${endpoint}`;

  const selectedCourseModules = useMemo(() => {
    if (!selectedCourseUniquePublicId) {
      return [];
    }
    return modulesByCourse[selectedCourseUniquePublicId] || [];
  }, [modulesByCourse, selectedCourseUniquePublicId]);

  const fetchPrerequisitesData = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(getApiUrl("/prerequisites"));
      const { courses = [], availableModules = [], modulesByCourse = {} } = data || {};

      setCourses(courses);
      setAvailableModules(availableModules);
      setModulesByCourse(modulesByCourse);
      if (!selectedCourseUniquePublicId && courses.length) {
        setSelectedCourseUniquePublicId(courses[0].uniquePublicId);
      }
    } catch (error) {
      showErrorNotification(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlink = async (uniquePublicId) => {
    try {
      await axios.post(getApiUrl("/unlink-module"), {
        courseUniquePublicId: selectedCourseUniquePublicId,
        moduleUniquePublicId: uniquePublicId
      });
      showSuccessNotification("Module unlinked");
      fetchPrerequisitesData();
    } catch (error) {
      showErrorNotification(error);
    }
  };

  useEffect(() => {
    fetchPrerequisitesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Edit and unlink controls for a linked module. Rendered as List.Item actions on
   * desktop, and in the card header on mobile.
   *
   * @param {Object} moduleLink - Linked module row.
   * @returns {Array<React.ReactNode>}
   */
  const renderModuleLinkActions = (moduleLink) => [
    <Button
      key="edit"
      size="small"
      icon={<EditOutlined />}
      onClick={() => {
        setActiveLinkData(moduleLink);
        setIsOpenCourseModuleLinkModal(true);
      }}
    />,
    <Popconfirm
      key="unlink"
      title="Unlink Module?"
      onConfirm={() => handleUnlink(moduleLink.uniquePublicId)}
      okText="Yes"
      cancelText="No"
      getPopupContainer={(node) => node.parentElement}
    >
      <Button size="small" danger icon={<DeleteOutlined />} />
    </Popconfirm>
  ];

  return (
    <Layout>
      <Content style={{ padding: clamp(12, 24) }}>
        <Card style={{ minHeight: "calc(100dvh - 100px)" }}>
          <Space
            wrap
            style={{
              width: "100%",
              justifyContent: "space-between",
              alignItems: "flex-end"
            }}
          >
            <Space direction="vertical" size={4}>
              <Title level={4} style={{ marginBottom: 0 }}>
                Course → Module Linking
              </Title>
              <Text type="secondary">Manage content structure and prerequisites</Text>

              <Space style={{ marginTop: 8 }}>
                <Text strong>Active Course:</Text>
                <Select
                  style={{ width: "min(260px, 100%)" }}
                  value={selectedCourseUniquePublicId}
                  onChange={setSelectedCourseUniquePublicId}
                  placeholder="Select course"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  options={courses.map((course) => ({
                    label: course.name,
                    value: course.uniquePublicId
                  }))}
                />
              </Space>
            </Space>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!selectedCourseUniquePublicId}
              onClick={() => {
                setActiveLinkData(null);
                setIsOpenCourseModuleLinkModal(true);
              }}
            >
              Link Module
            </Button>
          </Space>

          <Divider />

          <List
            loading={loading}
            // Mobile rows are already bordered cards, so the list divider is just noise
            split={!isMobileView}
            dataSource={selectedCourseModules}
            locale={{
              emptyText: "No modules linked to this course"
            }}
            rowKey="uniquePublicId"
            renderItem={(item, index) => {
              const {
                title,
                displayOrder,
                previousModuleUniquePublicId,
                nextModuleUniquePublicId,
                settings
              } = item;
              const { isVisible, isPreviewAllowed } = settings ?? {};

              const prevModule = selectedCourseModules.find(
                ({ uniquePublicId }) => uniquePublicId === previousModuleUniquePublicId
              );
              const nextModule = selectedCourseModules.find(
                ({ uniquePublicId }) => uniquePublicId === nextModuleUniquePublicId
              );

              const orderValue = (
                <Text>
                  <OrderedListOutlined style={{ marginRight: 4, fontSize: 12 }} />{" "}
                  {displayOrder ?? "—"}
                </Text>
              );

              const visibleValue = (
                <Text>
                  {isVisible ? (
                    <>
                      <EyeOutlined style={{ color: "#52C41A", marginRight: 4 }} /> Yes
                    </>
                  ) : (
                    <>
                      <EyeInvisibleOutlined style={{ color: "#FF4D4F", marginRight: 4 }} /> No
                    </>
                  )}
                </Text>
              );

              const previewValue = (
                <Text>
                  <PlayCircleOutlined
                    style={{ color: isPreviewAllowed ? "#1677FF" : "#FF4D4F", marginRight: 4 }}
                  />
                  {isPreviewAllowed ? "Enabled" : "Disabled"}
                </Text>
              );

              const previousValue = (
                <Text ellipsis={{ tooltip: prevModule?.title }} style={{ fontSize: "13px" }}>
                  <LeftCircleOutlined style={{ marginRight: 4 }} />
                  {prevModule?.title || "None"}
                </Text>
              );

              const nextValue = (
                <Text ellipsis={{ tooltip: nextModule?.title }} style={{ fontSize: "13px" }}>
                  <RightCircleOutlined style={{ marginRight: 4 }} />
                  {nextModule?.title || "None"}
                </Text>
              );

              // A phone cannot fit six fields side by side, so the row becomes a self-contained
              // card whose fields are stacked in an aligned label/value table.
              if (isMobileView) {
                return (
                  <List.Item style={{ padding: "8px 0" }}>
                    <Card
                      size="small"
                      style={{ width: "100%" }}
                      styles={{ title: { whiteSpace: "normal" }, body: { padding: 0 } }}
                      title={
                        <Space size={8} wrap>
                          <Text strong>
                            {index + 1}. {title}
                          </Text>
                          {!isVisible && <Tag icon={<EyeInvisibleOutlined />}>Hidden</Tag>}
                        </Space>
                      }
                      extra={<Space size={4}>{renderModuleLinkActions(item)}</Space>}
                    >
                      <Descriptions
                        bordered
                        size="small"
                        column={1}
                        styles={{
                          label: { width: 96, whiteSpace: "nowrap" },
                          content: { wordBreak: "break-word" }
                        }}
                        items={[
                          { key: "order", label: "Order", children: orderValue },
                          { key: "visible", label: "Visible", children: visibleValue },
                          { key: "preview", label: "Preview", children: previewValue },
                          { key: "previous", label: "Previous", children: previousValue },
                          { key: "next", label: "Next", children: nextValue }
                        ]}
                      />
                    </Card>
                  </List.Item>
                );
              }

              return (
                <List.Item style={{ padding: "16px 0" }} actions={renderModuleLinkActions(item)}>
                  <Row align="middle" style={{ width: "100%" }} gutter={[16, 12]}>
                    <Col lg={6}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Text strong style={{ fontSize: "15px" }}>
                          {index + 1}. {title}
                        </Text>
                        {!isVisible && <Tag icon={<EyeInvisibleOutlined />}>Hidden</Tag>}
                      </div>
                    </Col>

                    <Col lg={2}>
                      <LinkedEntityField label="Order">{orderValue}</LinkedEntityField>
                    </Col>

                    <Col lg={3}>
                      <LinkedEntityField label="Visible">{visibleValue}</LinkedEntityField>
                    </Col>

                    <Col lg={3}>
                      <LinkedEntityField label="Preview">{previewValue}</LinkedEntityField>
                    </Col>

                    <Col lg={5}>
                      <LinkedEntityField label="Previous">{previousValue}</LinkedEntityField>
                    </Col>

                    <Col lg={5}>
                      <LinkedEntityField label="Next">{nextValue}</LinkedEntityField>
                    </Col>
                  </Row>
                </List.Item>
              );
            }}
          />
        </Card>
      </Content>

      <CourseModuleLinkFormModal
        isOpenCourseModuleLinkFormModal={isOpenCourseModuleLinkModal}
        activeLinkData={activeLinkData}
        onCancel={() => {
          setActiveLinkData(null);
          setIsOpenCourseModuleLinkModal(false);
        }}
        onSuccess={() => {
          setActiveLinkData(null);
          setIsOpenCourseModuleLinkModal(false);
          fetchPrerequisitesData();
        }}
        getApiUrl={getApiUrl}
        courseUniquePublicId={selectedCourseUniquePublicId}
        availableModules={availableModules}
        linkedModules={selectedCourseModules}
      />
    </Layout>
  );
};

export default CourseModulesLinkingDashboard;
